<script setup lang="ts">
import { computed, ref } from 'vue';

import { assignIssue, unassignIssue } from '~/api/manageIssue';
import LoadingButton from '~/common/components/LoadingButton.vue';
import { MAX_ASSIGNED_ISSUES_BY_LABEL, MAX_ASSIGNED_ISSUES_BY_LEVEL, MAX_ASSIGNED_ISSUES_HARD_LIMIT } from '~/common/Constants';
import { useVueData } from '~/common/useVueData';

const { contributorLevel, isCurrentUsername } = useVueData();

const props = withDefaults(defineProps<{
  assignees: string[];
  issueIid: string;
  assignedIssueCount: number;
  assignedIssueCountsByLabel?: Partial<Record<string, number>>;
  isBlocked?: boolean;
  labels: string[];
  mentions: string[];
  projectId: string;
  webUrl: string;
}>(), {
  assignedIssueCountsByLabel: () => ({}),
  isBlocked: false,
});
const emits = defineEmits<{ error: [] }>();

// Constants.js is untyped, so its objects only accept the keys they happen to contain.
const maxIssuesByLevel: Partial<Record<number, number>> = MAX_ASSIGNED_ISSUES_BY_LEVEL;
const maxIssuesByLabel: Partial<Record<string, number>> = MAX_ASSIGNED_ISSUES_BY_LABEL;

const MAX_ISSUES = computed(() => maxIssuesByLevel[contributorLevel] ?? MAX_ASSIGNED_ISSUES_HARD_LIMIT);
const loading = ref(false);
const alreadyAssigned = computed(() => props.assignees.some(isCurrentUsername));
const issueLimitReached = computed(() => props.assignedIssueCount >= MAX_ISSUES.value);
const labelLimitReached = computed(() => {
  for (const label of props.labels) {
    const limit = maxIssuesByLabel[label];

    if (limit !== undefined && (props.assignedIssueCountsByLabel[label] ?? 0) >= limit) {
      return { label, limit };
    }
  }

  return null;
});
const renderAssignButton = computed(
  () => alreadyAssigned.value || (!props.isBlocked && !issueLimitReached.value && !labelLimitReached.value),
);

const workMessage = computed(() => {
  if (alreadyAssigned.value) {
    return 'You are assigned to this issue. If you are no longer able to work on it, please unassign yourself.';
  }

  if (props.isBlocked) {
    return 'This issue is marked blocked and likely isn\'t ready to be worked. Feel free to comment in the issue to confirm it is still blocked, and if work can be started.';
  }

  if (issueLimitReached.value) {
    return 'You have reached the maximum number of assigned issues. Please unassign yourself from an existing issue or close an issue before taking on a new one.';
  }

  if (labelLimitReached.value) {
    const { label, limit } = labelLimitReached.value;
    const issueWord = limit === 1 ? 'issue' : 'issues';

    return `
      You already have ${limit} open '${label}' ${issueWord} assigned to you. Please complete or be unassigned from it before taking on another.
      If your merge request is already in review, please ask a GitLab team member to assign additional '${label}' issues to you directly.
    `;
  }

  if (props.assignees.length) {
    return `This issue is already assigned to ${props.assignees.join(', and ')} but you're welcome to collaborate on it, or take it over if they are no longer able to work on it.`;
  }

  return `
    We would love you to help us move this issue forward.

    Sometimes it is not immediately clear whether an issue is ready for development.
    Whilst this does not block you from starting work,
    your merge request could be rejected if you do not have confirmation from the team beforehand.
  `;
});

const handleError = () => {
  loading.value = false;
  emits('error');
};

const manageIssue = async (): Promise<void> => {
  if (loading.value) {
    return;
  }

  const request = alreadyAssigned.value ? unassignIssue : assignIssue;

  loading.value = true;

  try {
    await request({
      issue_iid: props.issueIid,
      mentions: props.mentions,
      project_id: props.projectId,
    });
  } catch {
    handleError();

    return;
  }

  window.location.href = props.webUrl;
};
</script>

<template>
  {{ workMessage }}
  <div
    class="mt-2"
  >
    <LoadingButton
      v-if="renderAssignButton"
      :loading="loading"
      :loading-text="alreadyAssigned ? 'Unassigning...' : 'Assigning...'"
      variant="primary"
      @click="manageIssue"
    >
      {{ alreadyAssigned ? 'Unassign me' : 'Assign me' }}
    </LoadingButton>
  </div>
</template>
